import {Box, Typography} from '@mui/material';
import {
    BoltOutlined,
    CalendarTodayOutlined,
    CheckCircleOutlined,
    DescriptionOutlined,
    LinkOutlined,
    PersonOutlineOutlined,
    ReceiptLongOutlined,
} from '@mui/icons-material';
import {FC} from 'react';
import {FormattedMessage, useIntl} from 'react-intl';
import dayjs from 'dayjs';
import {ColoredStatus} from 'src/components/common/ColoredStatus';
import theme from 'src/theme';
import {
    footerItemSx,
    footerSx,
    heroAmountSx,
    heroCurrencySx,
    heroEyebrowSx,
    heroHeaderRowSx,
    heroMetaItemSx,
    heroMetaRowSx,
    heroSx,
    rootSx,
    statBodySx,
    statCardSx,
    statIconWrapperSx,
    statLabelSx,
    statValueSx,
    statsRowSx,
    statusChipSx,
    syncBadgeSx,
} from './InvoiceDetails.sx';
import {InvoiceDetailsType} from './InvoiceDetails.types';

const formatUserName = (user: InvoiceDetailsType['data']['user']): string => {
    if (!user) return '-';
    const fullName = `${user.firstName || ''} ${user.lastName || ''}`.trim();
    return fullName || user.emailAddress || '-';
};

export const InvoiceDetails: FC<InvoiceDetailsType> = ({data}) => {
    const intl = useIntl();

    const amount = data.bexioAmount || (data.amount / 100).toFixed(2);
    const consumedKWh = data.consumedWattsPoint
        ? (data.consumedWattsPoint / 1000).toFixed(3)
        : '0';
    const customerName = formatUserName(data.user);

    return (
        <Box sx={rootSx}>
            <Box sx={heroSx}>
                <Box sx={heroHeaderRowSx}>
                    <Typography sx={heroEyebrowSx}>
                        <FormattedMessage id="invoiceTotalAmount"/>
                    </Typography>
                    {data.status && (
                        <Box sx={statusChipSx}>
                            <ColoredStatus label={data.status} fontSize={12}/>
                        </Box>
                    )}
                </Box>
                <Box sx={{position: 'relative', zIndex: 1, display: 'flex', alignItems: 'baseline', flexWrap: 'wrap'}}>
                    <Typography sx={heroAmountSx} component="span">
                        {amount}
                    </Typography>
                    {data.currency && (
                        <Typography sx={heroCurrencySx} component="span">
                            {data.currency}
                        </Typography>
                    )}
                </Box>
                <Box sx={heroMetaRowSx}>
                    <Box sx={heroMetaItemSx}>
                        <ReceiptLongOutlined/>
                        {data.invoiceNumber || `#${data.id}`}
                    </Box>
                    {data.created && (
                        <Box sx={heroMetaItemSx}>
                            <CalendarTodayOutlined/>
                            {dayjs(data.created).format('DD MMM YYYY · HH:mm')}
                        </Box>
                    )}
                </Box>
            </Box>

            <Box sx={statsRowSx}>
                <Box sx={statCardSx}>
                    <Box sx={statIconWrapperSx(theme.palette.warning.main)}>
                        <BoltOutlined/>
                    </Box>
                    <Box sx={statBodySx}>
                        <Typography sx={statLabelSx}>
                            <FormattedMessage id="powerConsumed"/>
                        </Typography>
                        <Typography sx={statValueSx}>
                            {consumedKWh} {intl.formatMessage({id: 'kWh'})}
                        </Typography>
                    </Box>
                </Box>

                <Box sx={statCardSx}>
                    <Box sx={statIconWrapperSx(theme.palette.primary.main)}>
                        <PersonOutlineOutlined/>
                    </Box>
                    <Box sx={statBodySx}>
                        <Typography sx={statLabelSx}>
                            <FormattedMessage id="customer"/>
                        </Typography>
                        <Typography sx={statValueSx} title={customerName}>
                            {customerName}
                        </Typography>
                    </Box>
                </Box>

                <Box sx={statCardSx}>
                    <Box sx={statIconWrapperSx(theme.palette.secondary.main)}>
                        <DescriptionOutlined/>
                    </Box>
                    <Box sx={statBodySx}>
                        <Typography sx={statLabelSx}>
                            <FormattedMessage id="contract"/>
                        </Typography>
                        <Typography sx={statValueSx}>
                            {data.contractId ? `#${data.contractId}` : '-'}
                        </Typography>
                    </Box>
                </Box>
            </Box>

            <Box sx={footerSx}>
                <Box sx={footerItemSx}>
                    <LinkOutlined/>
                    <FormattedMessage id="invoiceBexioId"/>:
                    <Typography component="span" sx={{fontWeight: 600, color: 'text.primary'}}>
                        {data.bexioId || '-'}
                    </Typography>
                </Box>
                {data.issueFromBexio && (
                    <Box sx={syncBadgeSx}>
                        <CheckCircleOutlined/>
                        <FormattedMessage id="invoiceSyncedBexio"/>
                    </Box>
                )}
            </Box>
        </Box>
    );
};
